<template>
  <div
    ref="fullWidthStory"
    class="full-width-story critical-component-clear"
    :class="{
      [settings.desktop_layout_size]: !settings.is_full_height,
      [settings.mobile_layout_size]: isMobile,
      [`section-styles__bottom-margin--${settings.bottom_margin}`]:
        (isMobile && !settings.disable_mobile_padding)
    }"
  >
    <div
      v-if="isEmptyString(settings[getSettingKey('video')])"
      class="full-width-story__image-content"
      :class="{ 'full-width-story--full-height' : settings.is_full_height && !isMobile}"
    >
      <responsive-image
        block-class="full-width-story"
        :image="{
          src: settings[getSettingKey('image')],
        }"
        :image-class="(isSectionLoaded && !settings.disable_zoom_out) ? 'zoom-out' : ''"
        :padding="false"
        min-max="400-2000"
        sizes="120vw"
      />

      <div
        class="full-width-story__title-content container"
        :class="{ 'has-overlay': settings[getSettingKey('overlay')] }"
      >
        <div
          class="full-width-story__content full-width-story__content--mobile"
          :class="settings[getSettingKey('text_color')]"
        >
          <h2
            v-if="!isEmptyString(settings.subtitle)"
            class="
              full-width-story__subtitle
              text-heading-s
              text-heading-m-desktop
            "
            v-text="settings.subtitle"
          />

          <h3
            v-if="!isEmptyString(settings.title)"
            class="full-width-story__title text-heading-3xl"
            :class="{
              'text-heading-5xl-desktop' : settings.desktop_layout_size === 'desktop-large'
            }"
            v-text="settings.title"
          />

          <btn
            v-if="!isEmptyString(settings.button_text) && isInGridContent"
            class="full-width-story__button"
            :label="settings.button_text"
            :modifiers="buttonModifiers"
            type="button"
            @click="$emit('toggle-component')"
          />

          <btn
            v-if="!isEmptyString(settings.button_two_text) && isInGridContent"
            class="full-width-story__button"
            :label="settings.button_two_text"
            :modifiers="buttonModifiers"
            type="button"
            @click="$emit('toggle-component')"
          />
        </div>
      </div>
    </div>

    <responsive-video
      v-else
      block-class="full-width-story"
      class="full-width-story__video"
      :class="
        [
          settings[getSettingKey('text_color')],
          { 'full-width-story--full-height' : settings.is_full_height && !isMobile },
        ]
      "
      :fallback-image="settings[getSettingKey('video_fallback')]"
      :has-overlay="settings[getSettingKey('overlay')]"
      :reverse-controls-alignment="settings.content_alignment === 'right'"
      :video-src="settings[getSettingKey('video')]"
    >
      <div
        class="full-width-story__content full-width-story__content--mobile"
        :class="[
          `full-width-story__content--${settings.content_alignment}`,
          settings[getSettingKey('text_color')]
        ]"
      >
        <h2
          v-if="!isEmptyString(settings.subtitle)"
          class="
            full-width-story__subtitle
            text-heading-s
            text-heading-m-desktop
          "
          v-text="settings.subtitle"
        />

        <h3
          v-if="!isEmptyString(settings.title)"
          class="full-width-story__title text-heading-3xl"
          :class="{
            'text-heading-5xl-desktop' : settings.desktop_layout_size === 'desktop-large'
          }"
          v-text="settings.title"
        />

        <btn
          v-if="!isEmptyString(settings.button_text) && isInGridContent"
          class="full-width-story__button"
          :label="settings.button_text"
          :modifiers="buttonModifiers"
          type="button"
          @click="$emit('toggle-component')"
        />

        <btn
          v-if="!isEmptyString(settings.button_two_text) && isInGridContent"
          class="full-width-story__button"
          :label="settings.button_two_text"
          :modifiers="buttonModifiers"
          type="button"
          @click="$emit('toggle-component')"
        />
      </div>
    </responsive-video>

    <div
      class="full-width-story__content"
      :class="[
        `full-width-story__content--${settings.content_alignment}`,
        settings[getSettingKey('text_color')],
        { 'has-overlay': settings[getSettingKey('overlay')] }
      ]"
    >
      <div class="container full-width-story__content--container">
        <div class="full-width-story__content--desktop">
          <h2
            v-if="!isEmptyString(settings.subtitle)"
            class="
              full-width-story__subtitle
              text-heading-m-desktop
              text-heading-s
            "
            v-text="settings.subtitle"
          />

          <h3
            v-if="!isEmptyString(settings.title)"
            class="full-width-story__title text-heading-3xl"
            :class="{
              'text-heading-5xl-desktop' : settings.desktop_layout_size === 'desktop-large'
            }"
            v-text="settings.title"
          />
        </div>

        <div class="full-width-story__mobile-content-container">
          <p
            v-if="!isEmptyString(settings.description)"
            class="full-width-story__description text-body-s text-body-m-desktop"
            v-text="settings.description"
          />

          <div class="full-width-story__button-container">    
            <btn
              v-if="!isEmptyString(settings.button_text) && !isEmptyString(settings.button_link)"
              class="full-width-story__button"
              :label="settings.button_text"
              :url="settings.button_link"
              :modifiers="buttonModifiers"
            />

            <btn
              v-if="!isEmptyString(settings.button_two_text) && !isEmptyString(settings.button_two_link)"
              class="full-width-story__button"
              :label="settings.button_two_text"
              :url="settings.button_two_link"
              :modifiers="buttonModifiers"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Full width story (full-width-story)
 * -----------------------------------------------------------------------------
 * Full width story section.
 *
 * @param {Object} settings - Section's settings.
 * @param {Boolean} isInGridContent - Is in-grid content.
 *
 */
import { mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import ResponsiveVideo from '~global/responsive-video/responsive-video'

import { isElementVisible } from '~/helpers/general'

export default {
  name: 'FullWidthStory',

  components: {
    Btn,
    ResponsiveImage,
    ResponsiveVideo,
  },

  props: {
    settings: {
      type: Object,
      default: () => ({
        title: '',
        subtitle: '',
        description: '',
      }),
    },
    isInGridContent: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'toggle-component': () => true,
  },

  data() {
    return {
      isSectionLoaded: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Button modifiers
     * @returns {String}
     */
    buttonModifiers() {
      if (this.isMobile) {
        return this.settings.mode === 'dark' ? 'text-light' : 'text'
      }

      return this.settings[this.getSettingKey('text_color')] === 'white'
        ? 'text-light'
        : 'text'
    },
  },

  async mounted() {
    await this.$nextTick()
    this.handleScroll()

    window.addEventListener('scroll', () => this.handleScroll())
  },

  methods: {

    /**
     * Returns setting key with mobile/desktop prefix.
     * @param {String} key - Settings key.
     * @returns {String}
     */
    getSettingKey(key) {
      return this.isMobile ? `mobile_${key}` : `desktop_${key}`
    },

    /**
     * Returns true if text is empty string.
     * @param {String} text - Empty text.
     * @returns {Boolean}
     */
    isEmptyString(text) {
      return text === ''
    },

    /**
     * Change isSectionLoaded data to add zoom-in animation.
     */
    handleScroll() {
      this.isSectionLoaded = isElementVisible(this.$refs.fullWidthStory, 3)
    },
  },
}
</script>

<style lang="scss">
@import './full-width-story';
</style>
